import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  CheckCircle,
  Clock,
  AlertCircle,
  Loader,
  Shield,
  Trash2,
  Calendar,
  Info
} from 'lucide-react';

const DataDeletionStatus = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletionData, setDeletionData] = useState(null);
  const confirmationCode = searchParams.get('code');

  useEffect(() => {
    if (confirmationCode) {
      checkDeletionStatus(confirmationCode);
    } else {
      setLoading(false);
      setError('No confirmation code provided');
    }
  }, [confirmationCode]);

  const checkDeletionStatus = async (code) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-deletion-status?code=${code}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to check deletion status');
      }

      setDeletionData(data);
    } catch (err) {
      console.error('Error checking deletion status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'processing':
        return <Loader className="w-16 h-16 text-blue-500 animate-spin" />;
      case 'pending':
        return <Clock className="w-16 h-16 text-yellow-500" />;
      case 'failed':
        return <AlertCircle className="w-16 h-16 text-red-500" />;
      default:
        return <Info className="w-16 h-16 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'processing':
        return 'bg-blue-50 border-blue-200';
      case 'pending':
        return 'bg-yellow-50 border-yellow-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <>
      <Helmet>
        <title>Data Deletion Status - Ayur Parishkar Kendra</title>
        <meta name="description" content="Check the status of your Facebook data deletion request" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Shield className="w-20 h-20 text-green-600 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Data Deletion Status
            </h1>
            <p className="text-lg text-gray-600">
              Track your Facebook data deletion request
            </p>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {loading ? (
              <div className="text-center py-12">
                <Loader className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Checking deletion status...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
                <p className="text-gray-600 mb-6">{error}</p>
                <a
                  href="/"
                  className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Return to Home
                </a>
              </div>
            ) : deletionData ? (
              <div>
                {/* Status Icon and Message */}
                <div className={`text-center p-8 rounded-xl border-2 mb-8 ${getStatusColor(deletionData.status)}`}>
                  <div className="flex justify-center mb-4">
                    {getStatusIcon(deletionData.status)}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 capitalize">
                    {deletionData.status}
                  </h2>
                  <p className="text-gray-700 text-lg mb-2">
                    {deletionData.status_message}
                  </p>
                  {deletionData.estimated_completion && (
                    <p className="text-gray-600">
                      {deletionData.estimated_completion}
                    </p>
                  )}
                </div>

                {/* Confirmation Code */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Confirmation Code</p>
                  <p className="text-lg font-mono font-bold text-gray-900">
                    {deletionData.confirmation_code}
                  </p>
                </div>

                {/* Timeline */}
                <div className="space-y-4 mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Timeline
                  </h3>

                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">Request Received</p>
                        <p className="text-sm text-gray-600">
                          {new Date(deletionData.requested_at).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {deletionData.started_at && (
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        <div>
                          <p className="font-medium text-gray-900">Processing Started</p>
                          <p className="text-sm text-gray-600">
                            {new Date(deletionData.started_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    )}

                    {deletionData.completed_at && (
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                        <div>
                          <p className="font-medium text-gray-900">Deletion Completed</p>
                          <p className="text-sm text-gray-600">
                            {new Date(deletionData.completed_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <p className="text-sm text-gray-600 mt-2">
                    Time elapsed: {deletionData.hours_elapsed} hours
                  </p>
                </div>

                {/* Deletion Summary */}
                {deletionData.deletion_summary && (
                  <div className="mb-8 p-6 bg-green-50 rounded-lg border border-green-200">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                      <Trash2 className="w-5 h-5 text-green-600" />
                      Deletion Summary
                    </h3>
                    <div className="space-y-2">
                      <p className="text-gray-700">
                        <span className="font-medium">Total Records Deleted:</span>{' '}
                        {deletionData.deletion_summary.total_records_deleted}
                      </p>
                      <p className="text-gray-700">
                        <span className="font-medium">Tables Affected:</span>{' '}
                        {deletionData.deletion_summary.deleted_tables.join(', ')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {deletionData.error_message && (
                  <div className="mb-8 p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm font-medium text-red-800 mb-1">Error Details</p>
                    <p className="text-sm text-red-700">{deletionData.error_message}</p>
                  </div>
                )}

                {/* GDPR Compliance Info */}
                <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    GDPR Compliance Information
                  </h3>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p>
                      <span className="font-medium">Regulation:</span>{' '}
                      {deletionData.gdpr_compliance.regulation}
                    </p>
                    <p>
                      <span className="font-medium">Maximum Deletion Time:</span>{' '}
                      {deletionData.gdpr_compliance.max_deletion_time_days} days
                    </p>
                    <p>
                      <span className="font-medium">Contact:</span>{' '}
                      <a
                        href={`mailto:${deletionData.gdpr_compliance.contact_email}`}
                        className="text-blue-600 hover:underline"
                      >
                        {deletionData.gdpr_compliance.contact_email}
                      </a>
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-8 text-center">
                  <a
                    href="/"
                    className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Return to Home
                  </a>
                </div>
              </div>
            ) : null}
          </div>

          {/* Footer Note */}
          <div className="mt-8 text-center text-sm text-gray-600">
            <p>
              If you have any questions about your data deletion request, please contact us at{' '}
              <a href="mailto:privacy@ayurparishkarkendra.com" className="text-green-600 hover:underline">
                privacy@ayurparishkarkendra.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default DataDeletionStatus;

